import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { BehaviorSubject, Observable, takeUntil, withLatestFrom } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { NgxsFormPluginModule } from '@ngxs/form-plugin';
import { Actions, Select, Store, ofActionSuccessful } from '@ngxs/store';

import { CommandInvoke } from '@app/shared/actions/app-data.action';
import { GetUserProfileMe } from '@app/shared/actions/common-data.action';
import { WebApiResponse, WebApiResponseBase } from '@app/shared/classes/general';
import { BasedComponent } from '@app/shared/components/based.component';
import { MessageDataComponent } from '@app/shared/components/message-data/message-data.component';
import { SpinnerButtonComponent } from '@app/shared/components/spinner-button/spinner-button.component';
import { NgxsActionName, NgxsFormPath } from '@app/shared/constants';
import { AngularMaterialModule } from '@app/shared/modules/angular-material.module';
import { AppDataState } from '@app/shared/stores/app-data.state';
import { SiteConfigHelper } from '@app/shared/utils/site-config.helper';
import { UserProfile } from '@app/shared/classes/user-profile';

@Component({
  selector: 'app-supervisor-control-panel',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, SpinnerButtonComponent, AngularMaterialModule, MessageDataComponent, NgxsFormPluginModule, TranslateModule, ReactiveFormsModule],
  templateUrl: './supervisor-control-panel.component.html',
  styleUrl: './supervisor-control-panel.component.css'
})
export class SupervisorControlPanelComponent extends BasedComponent {
  store = inject(Store);
  formBuilder = inject(FormBuilder);
  actions$ = inject(Actions);

  editForm: FormGroup;
  formPath = NgxsFormPath.SUPERVISOR_CONTROL_PANEL_EDIT_FORM;
  jobSelectItems = SiteConfigHelper.GetEDLJobDropDownList();

  successMessageSub = new BehaviorSubject<string[]>([]);
  successMessage$ = this.successMessageSub.asObservable();
  errorMessageSub = new BehaviorSubject<string[]>([]);
  errorMessage$ = this.errorMessageSub.asObservable();

  @Select(AppDataState.taskResponse(NgxsActionName.GET_USER_PROFILE_ME)) response$: Observable<WebApiResponse<UserProfile[]>>;

  @Select(AppDataState.taskExecuting(NgxsActionName.COMMAND_INVOKE)) commandInvokeProcessing$: Observable<boolean>;
  @Select(AppDataState.taskExecuting(NgxsActionName.GET_USER_PROFILE_ME)) getUserProfileMeProcessing$: Observable<boolean>;

  @Select(AppDataState.taskError(NgxsActionName.COMMAND_INVOKE)) commandInvokeErrorResponse$: Observable<WebApiResponseBase>;
  @Select(AppDataState.taskError(NgxsActionName.GET_USER_PROFILE_ME)) getUserProfileMeErrorResponse$: Observable<WebApiResponseBase>;

  constructor() {
    super();
    this.initForm();
  }

  override ngOnInit() {
    super.ngOnInit();
    this.actions$.pipe(ofActionSuccessful(GetUserProfileMe), withLatestFrom(this.response$, this.getUserProfileMeErrorResponse$), takeUntil(this.destroy$)).subscribe(
      ([_, response, errorResponse]) => this.processSetUserProfileAfterUpdateObject(response, errorResponse));

    this.actions$.pipe(ofActionSuccessful(CommandInvoke), withLatestFrom(this.commandInvokeErrorResponse$), takeUntil(this.destroy$)).subscribe(
      ([_, errorResponse]) => this.processAfterUpdateObject(errorResponse));
  }

  initForm = () => {
    this.editForm = this.formBuilder.group({
      hackApexID: "",
      invokeCommands: []
    });
  }

  initMessage = () => {
    this.successMessageSub.next([]);
    this.errorMessageSub.next([]);
  }

  onSetHackApexID = () => {
    this.initMessage();
    var value = this.editForm.controls['hackApexID'].value;
    if (value) {
      this.store.dispatch(new GetUserProfileMe(value));
    }
  }

  onEDLJobStart = () => {
    this.initMessage();
    this.store.dispatch(new CommandInvoke());
  }

  processSetUserProfileAfterUpdateObject = (response: WebApiResponse<UserProfile[]>, errorResponse: WebApiResponseBase) => {
    if ((errorResponse && errorResponse.httpResponseStatus === 500)) {
      this.showErrorResult(errorResponse);
    }
    else if (response.data.length === 0) {
      this.errorMessageSub.next(["ml_143947"]);
    }
    else {
      this.showSuccessResult();
    }
  }

  processAfterUpdateObject = (errorResponse: WebApiResponseBase) => {
    if (errorResponse && errorResponse.httpResponseStatus === 500) {
      this.showErrorResult(errorResponse);
    }
    else {
      this.showSuccessResult();
    }
  }

  showSuccessResult = () => {
    this.successMessageSub.next(["ml_143937"]);
  }

  showErrorResult = (errorResponse: WebApiResponseBase) => {
    var errorMsg = this.getErrorMessage(errorResponse);
    this.errorMessageSub.next([errorMsg]);
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.successMessageSub.complete();
    this.errorMessageSub.complete();
  }
}
